<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase as Case, CustomerUser as User } from '@/types/generated'
  import { CasesService } from '@/types/generated/services.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useUserStore } from '@/store/userStore'
  import CaseTypeBadge from '@/components/app/cases/CaseTypeBadge.vue'
  import { Copy } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'

  const { t } = useTranslation({
    en: {
      inquiryData: 'Inquiry Data',
      caseWorker: 'Case Worker',
      legalField: 'Legal Field',
      legalTopic: 'Legal Topic',
      inquiryEmailAddress: 'Inquiry E-Mail Address',
      copy: 'Copy',
      createdBy: 'Created by',
      createdAt: 'on {date} at {time}',
      notSelected: 'Not selected',
      allLegalFields: 'All Legal Fields',
      allLegalTopics: 'All Legal Topics',
      allCaseWorkers: 'All Case Workers'
    },
    de: {
      inquiryData: 'Anfrage Daten',
      caseWorker: 'Sachbearbeiter',
      legalField: 'Rechtsgebiet',
      legalTopic: 'Rechtsthema',
      inquiryEmailAddress: 'Anfrage E-Mail Adresse',
      copy: 'Kopieren',
      createdBy: 'Erstellt von',
      createdAt: 'am {date} um {time} Uhr',
      notSelected: 'Nicht ausgewählt',
      allLegalFields: 'Alle Rechtsgebiete',
      allLegalTopics: 'Alle Rechtsthemen',
      allCaseWorkers: 'Alle Sachbearbeiter'
    }
  })

  // Format date and time for German locale
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
  }

  const formatTime = (dateString: string | undefined): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  }

  // Store setup
  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  // Options for select fields
  const legalFieldOptions = ref([])
  const caseWorkerOptions = ref([])
  const legalTopicOptions = ref([])

  // Selected values for select fields
  const selectedLegalField = ref('')
  const selectedCaseWorker = ref('')
  const selectedLegalTopic = ref('')

  const { toast } = useToast()

  const user = computed<User>(() => useUserStore().user as User)
  const { fetchLawFields, getLawTopicOptions, getLawFieldOptions } = useLawFieldStore()
  const lawFields = computed(() => useLawFieldStore().lawFields)
  const lawFieldOptions = computed<Option[]>(() => {
    return getLawFieldOptions(user.value)
  })
  const lawTopicOptions = computed<Option[]>(() => {
    return getLawTopicOptions(selectedLegalField.value)
  })
  // const lawTopicOptions = computed<Option[]>(() => {
  //   return getLawTopicOptions(createData.value.law.field)
  // })
  // Email address
  // const emailAddress = computed(() => caseRecord.value?.data?.contact?.email || '')

  // Watch for changes in caseRecord and update options
  watch(
    () => caseRecord.value?.org,
    (org) => {
      if (org) {
        legalFieldOptions.value = (org.law_fields || []).map((field) => ({
          value: field,
          title: field
        }))
        caseWorkerOptions.value = (org.users || [])
          .map((user) => ({
            value: user.uuid,
            title: `${user.first_name} ${user.last_name}`
          }))
          .sort((a, b) => a.title.localeCompare(b.title))

        // Assuming legal topics are available in org or could be derived from legal field
        legalTopicOptions.value = (org.legal_topics || []).map((topic) => ({
          value: topic,
          title: topic
        }))
      }
    },
    { immediate: true }
  )

  watch(
    () => caseRecord.value?.data?.law?.field,
    (field) => {
      selectedLegalField.value = field || ''
    },
    { immediate: true }
  )

  watch(
    () => caseRecord.value?.data?.law?.topic,
    (topic) => {
      selectedLegalTopic.value = topic || ''
    },
    { immediate: true }
  )

  watch(
    () => caseRecord.value?.clerk?.uuid,
    (clerkUuid) => {
      selectedCaseWorker.value = clerkUuid ?? ''
    },
    { immediate: true }
  )

  const handleFilterChange = async (value: any, fieldName: string) => {
    if (!caseRecord.value?.uuid) return

    try {
      let requestBody: any = {}

      if (fieldName === 'legalfield') {
        requestBody = {
          data: {
            law: {
              field: value
            }
          }
        }
      } else if (fieldName === 'legaltopic') {
        requestBody = {
          data: {
            law: {
              topic: value
            }
          }
        }
      } else if (fieldName === 'caseworker') {
        // Handle case worker assignment
        requestBody = {
          clerk_id: value || null
        }
      }

      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody
      })
    } catch (error) {
      console.error('Failed to update case:', error)
    }
  }

  const emailAddress = caseStore.caseRecord?.org.inbound_email_address

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress || '')
      toast({
        title: 'Erfolg',
        description: 'E-Mail Adresse wurde in die Zwischenablage kopiert',
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'E-Mail Adresse konnte nicht kopiert werden',
        variant: 'destructive'
      })
    }
  }

  // TODO: Fix the async
  onMounted(async () => {
    await fetchLawFields()
  })
</script>

<template>
  <div class="inquiry-data-widget flex flex-col gap-2">
    <div>
      <h3 class="pb-1">{{ t('createdBy') }}</h3>
      <div class="flex items-center gap-2">
        <CaseTypeBadge
          v-if="caseRecord?.type"
          :type="caseRecord.type"
        />
        <span>
          {{
            t('createdAt', {
              date: formatDate(caseRecord?.created_at),
              time: formatTime(caseRecord?.created_at)
            })
          }}
        </span>
      </div>
    </div>
    <!-- Case Worker Field -->
    <SelectField
      v-model="selectedCaseWorker"
      :options="caseWorkerOptions"
      :placeholder="t('caseWorker')"
      :allow-unselect="true"
      :unselected-option-text="t('notSelected')"
      name="caseworker"
      :label="t('caseWorker')"
      @update:model-value="(value) => handleFilterChange(value, 'caseworker')"
    />

    <div class="flex items-center gap-2">
      <!-- Legal Field -->
      <SelectField
        v-model="selectedLegalField"
        :options="lawFieldOptions"
        :placeholder="t('legalField')"
        :allow-unselect="true"
        :unselected-option-text="t('notSelected')"
        class="grow"
        name="legalfield"
        :label="t('legalField')"
        @update:model-value="(value) => handleFilterChange(value, 'legalfield')"
      />
      <!-- Legal Topic -->
      <SelectField
        v-model="selectedLegalTopic"
        :options="lawTopicOptions"
        :placeholder="t('legalTopic')"
        :allow-unselect="true"
        :unselected-option-text="t('notSelected')"
        class="grow"
        name="legaltopic"
        :label="t('legalTopic')"
        @update:model-value="(value) => handleFilterChange(value, 'legaltopic')"
      />
    </div>

    <!-- Email Address -->
    <div>
      <label class="mb-0 block text-sm font-medium text-gray-700">
        {{ t('inquiryEmailAddress') }}
      </label>
      <div class="flex gap-2">
        <div
          class="flex min-w-0 flex-grow items-center rounded-md border border-gray-200 bg-white px-3 py-2
            text-gray-900"
        >
          <span class="truncate">{{ emailAddress }}</span>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="default"
          class="gap-2"
          @click="copyToClipboard"
        >
          <Copy class="h-5 w-5 text-gray-800" />
          {{ t('copy') }}
        </Button>
      </div>
    </div>
  </div>
</template>
